// src/helpers/reportBuilderContextHelper.js
import { DIMENSION_FILTERS } from "../contexts/ReportBuilderContext";
import {
  EXIBE_NO_FLUXO_DEFAULT_ENTRY,
  isExibeNoFluxoDimension,
} from "./granularDimensions";
const MODE = { TODOS: 0, APENAS: 1, IGNORAR: 2 };
export const REPORT_BUILDER_DIMENSION_FILTER_OPTIONS = Object.freeze({
  widgetScopedExibeNoFluxo: true,
});

const uniq  = (arr=[]) => Array.from(new Set(arr));
const toSet = (a=[]) => new Set(a);
const inter = (a,b) => a.filter(x => b.has(x));
const diff  = (a,b) => a.filter(x => !b.has(x));
const union = (a,b) => uniq([...a, ...b]);
const toId = (v) => (v === null || v === undefined ? null : String(v));
const normalizeMode = (mode) => {
  const n = Number(mode);
  return n === MODE.APENAS || n === MODE.IGNORAR ? n : MODE.TODOS;
};

// lê ids do entry, aceitando "ids" (padrão), "values" e legado "value"
const readIds = (entry) =>
  uniq((entry?.ids || entry?.values || entry?.value || []).map(toId).filter(Boolean));

// === Mescla Relatório + Widget em 1 regra por dimensão ===
export function mergeReportAndWidgetFilter(reportEntry, widgetEntry) {
  const p = reportEntry || {};
  const c = widgetEntry || {};
  const pMode = normalizeMode(p.mode);
  const cMode = normalizeMode(c.mode);

  // combinações
  const P = readIds(p), C = readIds(c);
  const PS = toSet(P),   CS = toSet(C);

  // Tratamento especial: se o relatório tem APENAS mas sem valores, trata como TODOS
  if (pMode === MODE.APENAS && P.length === 0) {
    // Se widget também é TODOS, retorna null (ambos TODOS)
    if (cMode === MODE.TODOS) return null;
    // Senão, usa o filtro do widget
    return { mode: cMode, ids: C };
  }
  
  // Tratamento especial: se o widget tem APENAS mas sem valores, trata como TODOS
  if (cMode === MODE.APENAS && C.length === 0) {
    // Se relatório também é TODOS, retorna null (ambos TODOS)
    if (pMode === MODE.TODOS) return null;
    // Senão, usa o filtro do relatório
    return { mode: pMode, ids: P };
  }

  // casos simples (após tratamentos especiais)
  if (pMode === MODE.TODOS && cMode === MODE.TODOS) return null;
  if (pMode === MODE.TODOS && cMode !== MODE.TODOS) return { mode: cMode, ids: C };
  if (cMode === MODE.TODOS && pMode !== MODE.TODOS) return { mode: pMode, ids: P };

  // p=APENAS, c=APENAS  -> APENAS (P ∩ C)
  if (pMode === MODE.APENAS && cMode === MODE.APENAS) {
    return { mode: MODE.APENAS, ids: inter(P, CS) };
  }

  // p=APENAS, c=IGNORAR -> APENAS (P \ C)
  if (pMode === MODE.APENAS && cMode === MODE.IGNORAR) {
    return { mode: MODE.APENAS, ids: diff(P, CS) };
  }

  // p=IGNORAR, c=APENAS -> APENAS (C \ P)
  if (pMode === MODE.IGNORAR && cMode === MODE.APENAS) {
    return { mode: MODE.APENAS, ids: diff(C, PS) };
  }

  // p=IGNORAR, c=IGNORAR -> IGNORAR (P ∪ C)
  if (pMode === MODE.IGNORAR && cMode === MODE.IGNORAR) {
    return { mode: MODE.IGNORAR, ids: union(P, C) };
  }

  return null;
}

// Constrói payload final (array) a partir de relatório + widget
export function buildMergedDimensionFiltersPayload(
  widgetMap,
  reportMap,
  erp,
  syncType,
  options = {},
) {
  const safeW = (widgetMap && typeof widgetMap === 'object') ? widgetMap : {};
  const safeR = (reportMap && typeof reportMap === 'object') ? reportMap : {};
  const widgetScopedExibeNoFluxo = options?.widgetScopedExibeNoFluxo === true;

  const dims = [
    DIMENSION_FILTERS.CATEGORIA,
    DIMENSION_FILTERS.DEPARTAMENTO,
    DIMENSION_FILTERS.CONTA,
    DIMENSION_FILTERS.PROJETO,
    DIMENSION_FILTERS.EXIBE_NO_FLUXO,
    DIMENSION_FILTERS.EMPRESA,
    DIMENSION_FILTERS.PESSOA,
    DIMENSION_FILTERS.PRODUTO_SERVICO,
  ];

  const out = [];
  for (const dim of dims) {
    // PROJETO só vale quando erp === 0 (Omie)
    if (dim === DIMENSION_FILTERS.PROJETO && Number(erp) !== 0) continue;
    // EXIBE_NO_FLUXO só vale quando erp === 0 (Omie)
    if (dim === DIMENSION_FILTERS.EXIBE_NO_FLUXO && Number(erp) !== 0) continue;
    // EMPRESA só vale para sync consolidado (type === 2)
    if (dim === DIMENSION_FILTERS.EMPRESA && Number(syncType) !== 2) continue;
    // PRODUTO_SERVICO não suportado no Nibo (erp === 2)
    if (dim === DIMENSION_FILTERS.PRODUTO_SERVICO && Number(erp) === 2) continue;

    const merged = isExibeNoFluxoDimension(dim)
      ? (
          widgetScopedExibeNoFluxo
            ? (safeW[dim] || EXIBE_NO_FLUXO_DEFAULT_ENTRY)
            : mergeReportAndWidgetFilter(
                safeR[dim] || EXIBE_NO_FLUXO_DEFAULT_ENTRY,
                safeW[dim],
              )
        )
      : mergeReportAndWidgetFilter(safeR[dim], safeW[dim]);
    if (!merged) continue;
    const ids = readIds(merged);
    if (!ids.length) continue;
    out.push({
      granularDimension: dim,
      mode: normalizeMode(merged.mode),
      ids,
    });
  }
  
  return out;
}
